import React from 'react';

export const ConfidenceMeter = ({ confidence, phase, measurementStopped, equilibriumReached, equilibriumFading, unknownPressure = 0 }) => {
  const getColor = () => {
    if (equilibriumReached && !equilibriumFading) return '#71717A'; // Stopped: neutral gray
    if (confidence < 40) return '#2563EB'; // Blue - uncertain
    if (confidence < 60) return '#F59E0B'; // Orange - transition
    if (confidence < 85) return '#10B981'; // GREEN - porous zone (the sweet spot!)
    return '#EF4444'; // Red - danger
  };

  const color = getColor();
  
  // When measurement stops, the meter becomes still
  const meterStyle = measurementStopped && !equilibriumFading 
    ? { opacity: 0.7, filter: 'grayscale(30%)' }
    : {};

  // Combined pressure (confidence from bottom + unknown from top)
  const combinedPressure = Math.min(100, confidence + unknownPressure);
  
  // Effective max decreases as unknown pressure increases
  const effectiveMax = Math.max(0, Math.round(100 - unknownPressure));

  return (
    <div className="flex flex-col items-center gap-3" data-testid="confidence-meter" style={meterStyle}>
      <span className="text-xs uppercase tracking-widest font-mono text-zinc-400">
        {measurementStopped && !equilibriumFading ? '' : 'Confidence'}
      </span>

      <div className="relative w-10 bg-zinc-100 rounded-sm overflow-hidden border border-zinc-200"
        style={{ height: '280px' }}
      >
        {/* Unknown pressure from top (idle penalty) */}
        {unknownPressure > 0 && (
          <div
            className="absolute top-0 w-full rounded-sm transition-all duration-300 ease-out"
            style={{
              height: `${unknownPressure}%`,
              background: 'linear-gradient(180deg, #18181B 0%, #52525B 100%)',
              opacity: 0.85,
            }}
            data-testid="unknown-pressure-fill"
          />
        )}
        
        {/* Confidence from bottom */}
        <div
          className="absolute bottom-0 w-full rounded-sm transition-all duration-500 ease-out"
          style={{
            height: `${Math.min(100, confidence)}%`,
            backgroundColor: color,
          }}
          data-testid="confidence-fill"
        />

        {/* Threshold markers - hidden in equilibrium */}
        {(!measurementStopped || equilibriumFading) && [25, 50, 75].map(t => (
          <div
            key={t}
            className="absolute w-full border-t border-zinc-300 border-dashed"
            style={{ bottom: `${t}%` }}
          />
        ))}
        
        {/* Danger zone indicator when pressures are meeting */}
        {combinedPressure > 85 && unknownPressure > 5 && (
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(180deg, rgba(239,68,68,0.15) 0%, transparent 30%, transparent 70%, rgba(239,68,68,0.15) 100%)',
            }}
          />
        )}
      </div>

      <div className="text-center">
        {/* In equilibrium, number fades - no longer needed */}
        {(!measurementStopped || equilibriumFading) ? (
          <>
            <span
              className="text-2xl font-bold font-mono tabular-nums"
              style={{ color }}
              data-testid="confidence-value"
            >
              {Math.round(confidence)}
            </span>
            {/* Show effective max that decreases with unknown pressure */}
            <span className={`text-xs font-mono block ${unknownPressure > 0 ? 'text-zinc-600' : 'text-zinc-400'}`}>
              / {effectiveMax}
            </span>
          </>
        ) : (
          <span className="text-lg font-mono text-zinc-300">—</span>
        )}
      </div>

      {confidence > 85 && phase === 'building' && !measurementStopped && (
        <div className="text-xs text-red-500 font-mono uppercase tracking-wider">
          Danger
        </div>
      )}
      
      {/* Unknown approaching indicator */}
      {unknownPressure > 30 && phase === 'building' && (
        <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider">
          unknown stirs
        </div>
      )}
    </div>
  );
};
